// Pure, framework-free math for Science Mode readouts.
// Kept free of THREE.js so it tests without a WebGL context.

const DEG = 180 / Math.PI;
const UNIX_EPOCH_JD = 2440587.5;
const MS_PER_DAY = 86400000;

function length(v) {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}

function angleBetween(a, b) {
  const lenA = length(a);
  const lenB = length(b);
  if (lenA < 1e-12 || lenB < 1e-12) return null;
  const dot = a.x * b.x + a.y * b.y + a.z * b.z;
  const cos = Math.min(1, Math.max(-1, dot / (lenA * lenB)));
  return Math.acos(cos);
}

// Phase angle: angle Sun–body–observer, in degrees.
// Returns null if the body is at the sun position or the observer is at the body.
export function phaseAngleDeg(sunPos, bodyPos, observerPos) {
  const toSun = {
    x: sunPos.x - bodyPos.x,
    y: sunPos.y - bodyPos.y,
    z: sunPos.z - bodyPos.z
  };
  const toObs = {
    x: observerPos.x - bodyPos.x,
    y: observerPos.y - bodyPos.y,
    z: observerPos.z - bodyPos.z
  };
  const rad = angleBetween(toSun, toObs);
  return rad === null ? null : rad * DEG;
}

// Fraction of the disk that appears illuminated, as a percent.
// Classical formula: (1 + cos(phase)) / 2.
export function illuminationPercent(phaseDeg) {
  if (!Number.isFinite(phaseDeg)) return null;
  const rad = phaseDeg / DEG;
  return ((1 + Math.cos(rad)) / 2) * 100;
}

// Apparent angular diameter of a sphere with the given radius, viewed
// from `range` distance to its center. Degrees. Returns Infinity when
// the observer sits at or inside the sphere center.
export function apparentDiameterDeg(radius, range) {
  if (!Number.isFinite(radius) || !Number.isFinite(range) || range <= 0) return Infinity;
  return 2 * Math.atan(radius / range) * DEG;
}

// Julian Date for a JS Date (UTC). Uses the standard Unix-to-JD shift.
export function julianDate(date) {
  const ms = date instanceof Date ? date.getTime() : Number(date);
  if (!Number.isFinite(ms)) return NaN;
  return ms / MS_PER_DAY + UNIX_EPOCH_JD;
}
